import type { JsonFeedImportSource } from '../../types';

interface Props {
  source: JsonFeedImportSource;
  onChange: (updated: JsonFeedImportSource) => void;
}

/**
 * Editor for the extractor-output source. The payload is not fetched by the
 * admin: a watchkeeper-extractors run writes its ExtractionResult JSON to Cloud
 * Storage (a full brand crawl is megabytes, well past Firestore's 1 MiB limit),
 * the operator downloads it and uploads it in the import wizard, which unwraps
 * the envelope and hands the watches to the normal import pipeline.
 *
 * `feedUrl` is therefore a bookkeeping field — where this brand's result lands —
 * not something the admin reads. Automated pull is Phase 2.
 */
export function JsonFeedSourceEditor({ source, onChange }: Props) {
  return (
    <div className="space-y-3">
      <div className="text-[11px] text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
        Import Now opens the wizard on its file-upload step. Drop the extractor
        run&apos;s <code className="bg-white px-1 rounded">ExtractionResult</code>{' '}
        JSON there — it&apos;s unwrapped automatically and runs through the same
        calibre check, field rules, preview and overwrite gate as every other
        import. A result extracted for a different brand is rejected.
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Result location (optional)
        </label>
        <input
          type="text"
          value={source.feedUrl ?? ''}
          onChange={(e) =>
            onChange({ ...source, feedUrl: e.target.value || undefined })
          }
          placeholder="gs://…/extractions/omega/latest.json"
          className="w-full text-sm border border-gray-300 rounded px-2 py-1"
        />
        <p className="text-[11px] text-gray-400 mt-1">
          A note for yourself of where this brand&apos;s extractor output lands.
          Not fetched — the admin never pulls the file itself.
        </p>
      </div>
    </div>
  );
}
